import React, { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '../../../components/layout/Header';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import ConfirmationModal from '../../../components/shared/ConfirmationModal';
import { useOrganizationPixKey } from './hooks/useOrganizationPixKey';
import { usePermissions } from '../../../hooks/usePermissions';
import { maskPixKey } from '../../../domain/pix/maskPixKey';
import type { PixKeyType } from '../../../domain/pix/types';
import { PixKeyFormFields, PIX_TYPE_OPTIONS } from './components/PixKeyFormFields';

function getTypeLabel(type: PixKeyType): string {
    return PIX_TYPE_OPTIONS.find((option) => option.value === type)?.label ?? type;
}

const PixKeySettings: React.FC = () => {
    const { pixKey, loading, error, save, remove } = useOrganizationPixKey();
    const { canManagePixKey } = usePermissions();
    const [editing, setEditing] = useState(false);
    const [type, setType] = useState<PixKeyType>('cpf');
    const [key, setKey] = useState('');
    const [name, setName] = useState('');
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);

    const resetForm = useCallback(() => {
        if (pixKey) {
            setType(pixKey.pixType);
            setKey(pixKey.pixKey);
            setName(pixKey.pixName);
            return;
        }
        setType('cpf');
        setKey('');
        setName('');
    }, [pixKey]);

    useEffect(() => {
        if (!editing) resetForm();
    }, [pixKey, editing, resetForm]);

    const handleSave = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!key.trim() || !name.trim()) return;

        setSaving(true);
        setFormError(null);
        try {
            await save({ pixKey: key.trim(), pixType: type, pixName: name.trim() });
            setEditing(false);
        } catch (err) {
            setFormError(err instanceof Error ? err.message : 'Erro ao salvar chave PIX');
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async () => {
        try {
            await remove();
            setIsDeleteOpen(false);
            setEditing(false);
        } catch (err) {
            setFormError(err instanceof Error ? err.message : 'Erro ao remover chave PIX');
            setIsDeleteOpen(false);
        }
    };

    const showForm = canManagePixKey && (editing || !pixKey);
    const showView = Boolean(pixKey && !editing);

    return (
        <div className="flex h-full flex-col">
            <PageHeader
                title="Chave PIX"
                subtitle="Cadastre a chave PIX da organização para envio no chat."
                hideBackButton
            />

            {loading && <p className="text-secondary">Carregando...</p>}
            {!loading && error && <p className="text-destructive">{error}</p>}

            {!loading && !error && showView && pixKey && (
                <Card>
                    <Card.Header>
                        <div>
                            <Card.Title>{pixKey.pixName}</Card.Title>
                            <Card.Description>{getTypeLabel(pixKey.pixType)}</Card.Description>
                        </div>
                        {canManagePixKey && (
                            <div className="flex gap-2">
                                <Button variant="outline" onClick={() => { resetForm(); setFormError(null); setEditing(true); }}>Editar</Button>
                                <Button variant="outline" onClick={() => setIsDeleteOpen(true)}>Remover</Button>
                            </div>
                        )}
                    </Card.Header>
                    <Card.Content>
                        <p className="text-sm text-secondary">Chave</p>
                        <p className="font-mono text-primary">{maskPixKey(pixKey.pixKey)}</p>
                    </Card.Content>
                </Card>
            )}

            {!loading && !error && showForm && (
                <Card>
                    <form onSubmit={handleSave}>
                        <Card.Content className="space-y-4">
                            <PixKeyFormFields
                                type={type}
                                pixKey={key}
                                name={name}
                                onTypeChange={setType}
                                onPixKeyChange={setKey}
                                onNameChange={setName}
                                disabled={saving}
                            />
                            {formError && <p className="text-sm text-destructive">{formError}</p>}
                            <div className="flex flex-wrap gap-2">
                                {pixKey && (
                                    <Button type="button" variant="outline" onClick={() => setIsDeleteOpen(true)} disabled={saving}>
                                        Remover
                                    </Button>
                                )}
                                {pixKey && editing && (
                                    <Button type="button" variant="outline" onClick={() => { setFormError(null); setEditing(false); }} disabled={saving}>Cancelar</Button>
                                )}
                                <Button type="submit" disabled={saving || !key.trim() || !name.trim()}>
                                    {saving ? 'Salvando...' : 'Salvar'}
                                </Button>
                            </div>
                        </Card.Content>
                    </form>
                </Card>
            )}

            {!loading && !error && !pixKey && !canManagePixKey && (
                <Card>
                    <Card.Content>
                        <p className="text-secondary">Nenhuma chave PIX cadastrada. Peça a um admin.</p>
                    </Card.Content>
                </Card>
            )}

            <ConfirmationModal
                isOpen={isDeleteOpen}
                onClose={() => setIsDeleteOpen(false)}
                onConfirm={handleRemove}
                title="Remover chave PIX"
                message="Tem certeza que deseja remover a chave PIX da organização?"
                confirmButtonText="Remover"
                confirmButtonColor="red"
            />
        </div>
    );
};

export default PixKeySettings;
